/**
 * Meta Pixel - the browser half of the coded conversion events.
 *
 * Conversions are never decided here. The backend records each one when the
 * action is confirmed (account created, trial booked, subscription started)
 * and keeps it until a signed-in page claims it from
 * POST /api/meta-pixel/events/claim, which hands each event out exactly once.
 * This module only fires what the claim returns, with the server's event_id,
 * so the browser copy and the server's Conversions API copy deduplicate.
 * That is also why nothing here is tied to a click or a form submit, and why
 * a refresh, the back button or a second tab cannot fire anything twice.
 *
 * The base code loads only when the backend has the coded events switched on
 * for its environment AND this page's hostname is on the allow list it
 * returns - so localhost, staging and pre-prod builds never send to the live
 * dataset (REQ-3).
 *
 * No personal data is passed to `fbq('init')`: advanced matching happens on
 * the server, hashed. The browser only ever carries the hashed external_id.
 */

import { apiClient } from "@/services/apiClient";

export type MetaPixelConfig = {
  enabled: boolean;
  pixel_id: string | null;
  allowed_hosts: string[];
};

export type MetaBrowserEvent = {
  event_name: string;
  event_id: string;
  params: Record<string, unknown>;
};

export type MetaBrowserContext = {
  fbp: string | null;
  fbc: string | null;
  event_source_url: string | null;
};

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};

type PixelWindow = Window & { fbq?: Fbq; _fbq?: Fbq };

/** Dispatched on `window` to ask the mounted MetaPixel to claim right now. */
export const META_PIXEL_FLUSH_EVENT = "meta-pixel:flush";

const FBEVENTS_SRC = "https://connect.facebook.net/en_US/fbevents.js";

let configPromise: Promise<MetaPixelConfig | null> | null = null;
let initialisedPixelId: string | null = null;
let claimInFlight: Promise<void> | null = null;

/** Fetched once per page load; a failure means "do not load the pixel". */
export function fetchMetaPixelConfig(): Promise<MetaPixelConfig | null> {
  if (!configPromise) {
    configPromise = apiClient
      .get<MetaPixelConfig>("/api/meta-pixel/config", { skipAuthRefresh: true })
      .then((response) => response.data ?? null)
      .catch(() => null);
  }

  return configPromise;
}

export function isPixelAllowedOnHost(
  hostname: string,
  config: MetaPixelConfig | null,
): config is MetaPixelConfig & { pixel_id: string } {
  if (!config?.enabled || !config.pixel_id) {
    return false;
  }

  const host = hostname.trim().toLowerCase();

  return config.allowed_hosts.some((allowed) => allowed.toLowerCase() === host);
}

/**
 * Meta's standard base code, plus init and the PageView. Idempotent: the
 * snippet itself bails if `fbq` already exists, and init runs once per id.
 * fbevents.js then reports client-side route changes as PageViews itself.
 */
export function loadMetaPixel(pixelId: string): void {
  if (typeof window === "undefined") {
    return;
  }

  const w = window as PixelWindow;

  if (!w.fbq) {
    const fbq: Fbq = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        fbq.queue!.push(args);
      }
    };
    if (!w._fbq) {
      w._fbq = fbq;
    }
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    w.fbq = fbq;

    const script = document.createElement("script");
    script.async = true;
    script.src = FBEVENTS_SRC;
    document.head.appendChild(script);
  }

  if (initialisedPixelId !== pixelId) {
    initialisedPixelId = pixelId;

    // Another installation (the GTM pixel tags, until they are paused at
    // cut-over) may already have initialised this pixel and sent the
    // PageView. A second init is ignored by Meta, but a second PageView is
    // counted, so in that case only the coded events go through this module.
    if (!isPixelInitialisedElsewhere(w.fbq!, pixelId)) {
      w.fbq!("init", pixelId);
      w.fbq!("track", "PageView");
    }
  }
}

/**
 * Whether `pixelId` was already initialised on this page, either by a loaded
 * fbevents.js (getState) or by a stub whose queue still holds the init.
 */
function isPixelInitialisedElsewhere(fbq: Fbq, pixelId: string): boolean {
  try {
    const state = (fbq as Fbq & { getState?: () => { pixels?: { id?: unknown }[] } }).getState?.();
    if (state?.pixels?.some((pixel) => String(pixel?.id) === pixelId)) {
      return true;
    }

    return (fbq.queue ?? []).some((call) => {
      const args = Array.from(call as ArrayLike<unknown>);
      return args[0] === "init" && String(args[1]) === pixelId;
    });
  } catch {
    return false;
  }
}

export function isMetaPixelLoaded(): boolean {
  return initialisedPixelId !== null;
}

/** fbq('track', name, params, { eventID }) - the server's id, never a new one. */
export function fireMetaEvents(events: MetaBrowserEvent[]): void {
  const fbq = (window as PixelWindow).fbq;

  if (!fbq || !isMetaPixelLoaded()) {
    return;
  }

  for (const event of events) {
    if (!event?.event_name || !event?.event_id) {
      continue;
    }

    try {
      fbq("track", event.event_name, event.params ?? {}, {
        eventID: event.event_id,
      });
    } catch {
      // A broken fbevents.js must never surface in the page.
    }
  }
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const match = document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${name}=`));

  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

/**
 * The browser identifiers the server copy needs for matching. `_fbc` is
 * normally written by fbevents.js; if it has not been yet but the landing URL
 * carries an fbclid, build it in Meta's documented format.
 */
export function readMetaBrowserContext(): MetaBrowserContext {
  if (typeof window === "undefined") {
    return { fbp: null, fbc: null, event_source_url: null };
  }

  let fbc = readCookie("_fbc");

  if (!fbc) {
    const fbclid = new URLSearchParams(window.location.search).get("fbclid");
    if (fbclid) {
      fbc = `fb.1.${Date.now()}.${fbclid}`;
    }
  }

  return {
    fbp: readCookie("_fbp"),
    fbc,
    event_source_url: window.location.href,
  };
}

/**
 * Claim whatever the server holds for this account and fire it. Single-flight
 * so overlapping triggers (mount, user change, explicit flush) share one
 * request; the server's one-time claim is what actually guarantees "once".
 */
export function claimAndFireMetaEvents(): Promise<void> {
  if (!isMetaPixelLoaded()) {
    return Promise.resolve();
  }

  if (!claimInFlight) {
    claimInFlight = apiClient
      .post<{ events?: MetaBrowserEvent[] }>(
        "/api/meta-pixel/events/claim",
        readMetaBrowserContext(),
      )
      .then((response) => fireMetaEvents(response.data?.events ?? []))
      .catch(() => {
        // Unclaimed events stay on the server for the next page load; the
        // server copy is sent regardless.
      })
      .finally(() => {
        claimInFlight = null;
      });
  }

  return claimInFlight;
}

/**
 * Ask the mounted MetaPixel to claim now - called right after an action the
 * backend has just confirmed, when no navigation or sign-in will trigger it.
 */
export function requestMetaPixelFlush(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(META_PIXEL_FLUSH_EVENT));
  }
}

/** Test-only: forget the per-page-load caches. */
export function resetMetaPixelForTests(): void {
  configPromise = null;
  initialisedPixelId = null;
  claimInFlight = null;
}
